import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  SUPPORT_CHAT_CATEGORIES,
  SUPPORT_CHAT_OPEN_EVENT,
  SUPPORT_CHAT_STORAGE_KEY,
  getSupportCategoryLabel,
} from '../lib/supportChatConfig';
import {
  createSupportChat,
  fetchSupportChat,
  fetchSupportMessages,
  sendSupportMessage,
  uploadSupportAttachment,
} from '../lib/supportChatApi';
import {
  subscribeToSupportChat,
  subscribeToSupportMessages,
} from '../lib/supportChatFirestore';

const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

const readStoredChat = () => {
  try {
    const raw = window.localStorage.getItem(SUPPORT_CHAT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeChat = ({ chatId, accessToken }) => {
  window.localStorage.setItem(
    SUPPORT_CHAT_STORAGE_KEY,
    JSON.stringify({ chatId, accessToken, updatedAt: Date.now() })
  );
};

const clearStoredChat = () => {
  window.localStorage.removeItem(SUPPORT_CHAT_STORAGE_KEY);
};

const formatTime = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const isImageAttachment = (message) => {
  const type = String(message.attachment_type || '').toLowerCase();
  const url = String(message.attachment_url || '').toLowerCase();
  return type.startsWith('image/') || /\.(jpg|jpeg|png|webp|gif|heic|heif)(\?|$)/.test(url);
};

const normalizeStatus = (status) => String(status || 'open').toLowerCase();

const formatStatusLabel = (status) => {
  const normalized = normalizeStatus(status);
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

export default function SupportChatWidget() {
  const { initializing, isAuthenticated, getToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('categories');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pendingStarter, setPendingStarter] = useState(null);
  const [guestForm, setGuestForm] = useState({ name: '', email: '', phone: '' });
  const [chat, setChat] = useState(null);
  const [accessToken, setAccessToken] = useState('');
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesScrollRef = useRef(null);
  const pollingStartedRef = useRef(false);
  const chatStatus = normalizeStatus(chat?.status);
  const canSendMessage = Boolean(chat?.id) && chatStatus === 'open';
  const latestMessageMarker = messages.length
    ? `${messages[messages.length - 1]?.id || messages.length}-${messages[messages.length - 1]?.created_at || ''}`
    : 'empty';

  const getAuthToken = useCallback(async () => {
    if (!isAuthenticated) return null;
    return getToken();
  }, [getToken, isAuthenticated]);

  const startConversation = useCallback(async (category, options = {}) => {
    const categoryKey = category || selectedCategory || 'general_enquiry';
    const token = await getAuthToken();
    const guestInfo = options.guestInfo || guestForm;
    const payload = {
      category: categoryKey,
      context: options.context || null,
      initialMessage: options.initialMessage || '',
    };

    if (!token) {
      payload.name = guestInfo.name;
      payload.email = guestInfo.email;
      payload.phone = guestInfo.phone;
    }

    setBusy(true);
    setError('');

    try {
      const response = await createSupportChat(payload, { token });
      const nextChat = response.chat || { id: response.chatId, category: categoryKey };
      const nextAccessToken = response.accessToken || response.access_token || nextChat.access_token || '';
      setChat(nextChat);
      setAccessToken(nextAccessToken);
      setMessages([]);
      setView('conversation');
      storeChat({ chatId: nextChat.id || response.chatId, accessToken: nextAccessToken });
    } catch (startError) {
      setError(startError.message || 'Unable to start support chat.');
    } finally {
      setBusy(false);
    }
  }, [getAuthToken, guestForm, selectedCategory]);

  useEffect(() => {
    if (initializing) return undefined;

    let cancelled = false;
    const stored = readStoredChat();
    if (!stored?.chatId) return undefined;

    async function restoreChat() {
      try {
        const token = await getAuthToken();
        const restored = await fetchSupportChat(stored.chatId, {
          token,
          accessToken: stored.accessToken,
        });
        if (cancelled) return;
        if (normalizeStatus(restored.status) === 'archived') {
          clearStoredChat();
          setChat(null);
          setAccessToken('');
          setMessages([]);
          setView('categories');
          return;
        }
        setChat(restored);
        setAccessToken(stored.accessToken || '');
        setSelectedCategory(restored.category || '');
        setView('conversation');
      } catch {
        clearStoredChat();
        if (!cancelled) {
          setChat(null);
          setAccessToken('');
          setMessages([]);
          setView('categories');
        }
      }
    }

    restoreChat();
    return () => {
      cancelled = true;
    };
  }, [getAuthToken, initializing]);

  useEffect(() => {
    const handleOpenRequest = (event) => {
      const detail = event.detail || {};
      const category = detail.category || detail.categoryKey || '';
      setIsOpen(true);
      setError('');

      if (chat?.id && normalizeStatus(chat.status) === 'open') {
        setPendingStarter(null);
        setSelectedCategory(chat.category || '');
        setView('conversation');
        return;
      }

      if (!category) {
        setView(chat ? 'conversation' : 'categories');
        return;
      }

      setSelectedCategory(category);
      setPendingStarter({
        category,
        context: detail.context || null,
        initialMessage: detail.initialMessage || '',
      });
    };

    window.addEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenRequest);
    return () => window.removeEventListener(SUPPORT_CHAT_OPEN_EVENT, handleOpenRequest);
  }, [chat]);

  useEffect(() => {
    if (!pendingStarter || initializing) return;

    if (isAuthenticated) {
      const starter = pendingStarter;
      setPendingStarter(null);
      startConversation(starter.category, {
        context: starter.context,
        initialMessage: starter.initialMessage,
      });
      return;
    }

    setView('guest');
  }, [initializing, isAuthenticated, pendingStarter, startConversation]);

  useEffect(() => {
    if (!chat?.id) return undefined;

    let cancelled = false;
    let unsubscribeChat = null;
    let pollTimer = null;

    const pollChat = async () => {
      try {
        const token = await getAuthToken();
        const latest = await fetchSupportChat(chat.id, { token, accessToken });
        if (!cancelled) {
          if (normalizeStatus(latest.status) === 'archived') {
            clearStoredChat();
            setChat(null);
            setAccessToken('');
            setMessages([]);
            setView('categories');
            return;
          }
          setChat(latest);
        }
      } catch {
        if (!cancelled) {
          clearStoredChat();
          setChat(null);
          setAccessToken('');
          setMessages([]);
          setView('categories');
        }
      }
    };

    subscribeToSupportChat(chat.id, (latest) => {
      if (!cancelled) {
        if (normalizeStatus(latest.status) === 'archived') {
          clearStoredChat();
          setChat(null);
          setAccessToken('');
          setMessages([]);
          setView('categories');
          return;
        }
        setChat((current) => ({ ...current, ...latest }));
      }
    }, () => {
      if (!pollTimer) {
        pollTimer = window.setInterval(pollChat, 8000);
      }
    })
      .then((unsubscribe) => {
        unsubscribeChat = unsubscribe;
      })
      .catch(() => {
        pollTimer = window.setInterval(pollChat, 8000);
      });

    return () => {
      cancelled = true;
      if (unsubscribeChat) unsubscribeChat();
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [accessToken, chat?.id, getAuthToken]);

  useEffect(() => {
    if (!chat?.id) return undefined;

    let cancelled = false;
    let unsubscribeMessages = null;
    let pollTimer = null;
    pollingStartedRef.current = false;

    const loadMessages = async () => {
      try {
        const token = await getAuthToken();
        const rows = await fetchSupportMessages(chat.id, { token, accessToken });
        if (!cancelled) setMessages(rows);
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || 'Unable to load messages.');
      }
    };

    const startPolling = () => {
      if (pollingStartedRef.current) return;
      pollingStartedRef.current = true;
      loadMessages();
      pollTimer = window.setInterval(loadMessages, 5000);
    };

    loadMessages();
    subscribeToSupportMessages(chat.id, (rows) => {
      if (!cancelled) setMessages(rows);
    }, startPolling)
      .then((unsubscribe) => {
        unsubscribeMessages = unsubscribe;
      })
      .catch(startPolling);

    return () => {
      cancelled = true;
      if (unsubscribeMessages) unsubscribeMessages();
      if (pollTimer) window.clearInterval(pollTimer);
    };
  }, [accessToken, chat?.id, getAuthToken]);

  useEffect(() => {
    if (!isOpen || view !== 'conversation') return undefined;
    const frame = window.requestAnimationFrame(() => {
      const node = messagesScrollRef.current;
      if (node) {
        node.scrollTop = node.scrollHeight;
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [chat?.id, isOpen, latestMessageMarker, view]);

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setError('');

    if (chat?.id && normalizeStatus(chat.status) === 'open') {
      setView('conversation');
      setError('You already have an open support conversation.');
      return;
    }

    if (initializing) {
      setPendingStarter({ category, context: null, initialMessage: '' });
      setView('guest');
      return;
    }

    if (isAuthenticated) {
      startConversation(category);
      return;
    }

    setView('guest');
  };

  const handleGuestSubmit = (event) => {
    event.preventDefault();
    if (chat?.id && normalizeStatus(chat.status) === 'open') {
      setView('conversation');
      setError('You already have an open support conversation.');
      return;
    }
    startConversation(selectedCategory || pendingStarter?.category || 'general_enquiry', {
      guestInfo: guestForm,
      context: pendingStarter?.context || null,
      initialMessage: pendingStarter?.initialMessage || '',
    });
    setPendingStarter(null);
  };

  const handleGuestChange = (event) => {
    const { name, value } = event.target;
    setGuestForm((current) => ({ ...current, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    setError('');

    if (file && file.size > MAX_ATTACHMENT_BYTES) {
      setError('Attachments must be 15MB or smaller.');
      event.target.value = '';
      return;
    }

    setAttachment(file);
  };

  const clearAttachment = () => {
    setAttachment(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleStartNewConversation = () => {
    clearStoredChat();
    setChat(null);
    setAccessToken('');
    setMessages([]);
    setDraft('');
    setSelectedCategory('');
    setPendingStarter(null);
    clearAttachment();
    setError('');
    setView('categories');
  };

  const handleSend = async (event) => {
    event.preventDefault();
    const text = draft.trim();

    if (!canSendMessage || (!text && !attachment)) return;

    setBusy(true);
    setError('');

    try {
      const token = await getAuthToken();
      let uploaded = null;

      if (attachment) {
        setUploading(true);
        uploaded = await uploadSupportAttachment(attachment, {
          chatId: chat.id,
          token,
          accessToken,
        });
      }

      await sendSupportMessage(chat.id, {
        message: text,
        attachment_url: uploaded?.url || null,
        attachment_name: uploaded?.original_name || attachment?.name || null,
        attachment_type: uploaded?.mime_type || attachment?.type || null,
      }, { token, accessToken });

      setDraft('');
      clearAttachment();
      const latest = await fetchSupportMessages(chat.id, { token, accessToken });
      setMessages(latest);
    } catch (sendError) {
      setError(sendError.message || 'Unable to send message.');
    } finally {
      setUploading(false);
      setBusy(false);
    }
  };

  const renderCategories = () => (
    <div className="support-chat-panel-body">
      <p className="support-chat-prompt">How can we help you?</p>
      <div className="support-chat-categories">
        {SUPPORT_CHAT_CATEGORIES.map((category) => (
          <button
            key={category.key}
            type="button"
            className="support-chat-category"
            onClick={() => handleCategorySelect(category.key)}
            disabled={busy}
          >
            {category.label}
          </button>
        ))}
      </div>
    </div>
  );

  const renderGuestForm = () => (
    <form className="support-chat-panel-body support-chat-guest-form" onSubmit={handleGuestSubmit}>
      <button type="button" className="support-chat-back" onClick={() => setView('categories')}>
        Back
      </button>
      <div>
        <p className="support-chat-prompt">{getSupportCategoryLabel(selectedCategory || pendingStarter?.category)}</p>
        <p className="support-chat-note">Enter your contact details so our team can follow up if you step away.</p>
      </div>
      <label>
        Name *
        <input name="name" value={guestForm.name} onChange={handleGuestChange} required />
      </label>
      <label>
        Email *
        <input type="email" name="email" value={guestForm.email} onChange={handleGuestChange} required />
      </label>
      <label>
        Phone
        <input type="tel" name="phone" value={guestForm.phone} onChange={handleGuestChange} />
      </label>
      <button type="submit" className="support-chat-primary" disabled={busy}>
        {busy ? 'Starting...' : 'Start chat'}
      </button>
    </form>
  );

  const renderConversation = () => {
    if (chatStatus === 'archived') {
      return (
        <div className="support-chat-panel-body support-chat-closed-state">
          <p className="support-chat-prompt">This conversation is no longer available.</p>
          <p className="support-chat-note">Start a new support request and our team will pick it up from there.</p>
          <button type="button" className="support-chat-primary" onClick={handleStartNewConversation}>
            Start New Conversation
          </button>
        </div>
      );
    }

    return (
    <div className="support-chat-conversation">
      <div className="support-chat-conversation-meta">
        <span>Support Conversation</span>
        <strong className={`support-chat-status support-chat-status--${chatStatus}`}>
          Status: {formatStatusLabel(chatStatus)}
        </strong>
      </div>

      <div className="support-chat-messages" ref={messagesScrollRef} aria-live="polite">
        {messages.length === 0 && (
          <p className="support-chat-empty">Your conversation will appear here.</p>
        )}
        {messages.map((message) => (
          <article
            key={message.id}
            className={`support-chat-message support-chat-message--${message.sender === 'admin' ? 'admin' : 'customer'}`}
          >
            <div className="support-chat-message-bubble">
              {message.message && <p>{message.message}</p>}
              {message.attachment_url && (
                <a
                  className="support-chat-attachment"
                  href={message.attachment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {isImageAttachment(message) ? (
                    <img src={message.attachment_url} alt={message.attachment_name || 'Support attachment'} />
                  ) : (
                    <span>{message.attachment_name || 'Open attachment'}</span>
                  )}
                </a>
              )}
            </div>
            <time>{formatTime(message.created_at)}</time>
          </article>
        ))}
      </div>

      {chatStatus === 'closed' ? (
        <div className="support-chat-closed-notice">
          <p>This conversation has been closed.</p>
          <button type="button" className="support-chat-primary" onClick={handleStartNewConversation}>
            Start New Conversation
          </button>
        </div>
      ) : (
        <form className="support-chat-composer" onSubmit={handleSend}>
          {attachment && (
            <div className="support-chat-file-chip">
              <span>{attachment.name}</span>
              <button type="button" onClick={clearAttachment} aria-label="Remove attachment">
                x
              </button>
            </div>
          )}
          <div className="support-chat-compose-row">
            <label className="support-chat-file-button" title="Attach file">
              +
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf"
                onChange={handleFileChange}
                disabled={!canSendMessage || busy}
              />
            </label>
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={1}
              placeholder="Type your message"
              disabled={!canSendMessage || busy}
            />
            <button type="submit" className="support-chat-send" disabled={busy || (!draft.trim() && !attachment)}>
              {uploading ? 'Uploading' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </div>
    );
  };

  const headerCategory = chat?.id
    ? getSupportCategoryLabel(chat.category || selectedCategory)
    : selectedCategory
      ? getSupportCategoryLabel(selectedCategory)
      : 'Customer Support';

  return (
    <div className={`support-chat-widget${isOpen ? ' support-chat-widget--open' : ''}`}>
      {isOpen && (
        <section className="support-chat-panel" aria-label="Customer support chat">
          <header className="support-chat-header">
            <div>
              <p>Support Conversation</p>
              <h2>{headerCategory}</h2>
              {chat?.id && (
                <span className={`support-chat-header-status support-chat-status--${chatStatus}`}>
                  Status: {formatStatusLabel(chatStatus)}
                </span>
              )}
            </div>
            <div className="support-chat-header-actions">
              <button type="button" onClick={() => setIsOpen(false)} aria-label="Collapse support chat">
                -
              </button>
            </div>
          </header>

          {error && <p className="support-chat-error" role="alert">{error}</p>}
          {view === 'categories' && renderCategories()}
          {view === 'guest' && renderGuestForm()}
          {view === 'conversation' && renderConversation()}
        </section>
      )}

      <button
        type="button"
        className="support-chat-bubble"
        onClick={() => {
          setIsOpen(true);
          setView(chat ? 'conversation' : 'categories');
        }}
        aria-label="Open customer support chat"
      >
        <span>Support</span>
      </button>
    </div>
  );
}
